// Settings pages the global search bar can open.
//
// Matching is a static keyword map (no AI): every entry has a title, the Settings
// tab it lives in, the page it opens and the words people are likely to type for it.
// To add a page, add an entry here.
//
// Entries are only returned to users who can open Settings (same rule as the
// /financial-setting route), so users without the permission never see them.

const FINANCIAL = "Financial";
const MEMBERS = "Members";
const ORGANIZATION = "Organization info";

export const SETTINGS_SEARCH_ENTRIES = [
  // Organization info
  {
    id: "organization-info",
    title: "Organization Info",
    section: ORGANIZATION,
    url: "/financial-setting?tab=organization",
    keywords: ["organization", "org", "profile", "email", "phone", "address", "logo", "contact details"],
  },
  {
    id: "organization-links",
    title: "Organization Links",
    section: ORGANIZATION,
    url: "/financial-setting?tab=organization",
    keywords: ["links", "member site", "donation page", "education page", "payment page", "event calendar", "iframe", "embed", "url"],
  },
  {
    id: "payment-page-options",
    title: "Payment Page Options",
    section: ORGANIZATION,
    url: "/financial-setting?tab=organization",
    keywords: ["external payment", "allow installments", "edit amount", "bill my account", "announcement", "portal", "app"],
  },

  // Financial
  {
    id: "account-billing-types",
    title: "Account Billing Types",
    section: FINANCIAL,
    url: "/financial-setting/account-billing-types/view",
    keywords: ["billing", "billing type", "billing types", "statement", "invoice type"],
  },
  {
    id: "bank-accounts",
    title: "Bank Accounts",
    section: FINANCIAL,
    url: "/financial-setting/bank-accounts/view",
    keywords: ["bank", "deposit account", "checking", "routing"],
  },
  {
    id: "ledger-accounts",
    title: "Ledger Accounts",
    section: FINANCIAL,
    url: "/financial-setting/ledger-accounts/view",
    keywords: ["ledger", "ledgers", "sub ledger", "sub-ledger", "gl", "general ledger", "chart of accounts", "coa"],
  },
  {
    id: "payment-types",
    title: "Payment Types",
    section: FINANCIAL,
    url: "/financial-setting/payment-types/view",
    keywords: ["payment methods", "cash", "check", "ach", "credit card", "convenience fee"],
  },
  {
    id: "reversal-types",
    title: "Reversal Types",
    section: FINANCIAL,
    url: "/financial-setting/reversal-types/view",
    keywords: ["reversal", "refund", "void", "chargeback"],
  },
  {
    id: "gateway-settings",
    title: "Gateway Settings",
    section: FINANCIAL,
    url: "/financial-setting/provider-info/view",
    keywords: ["gateway", "payment gateway", "processor", "provider", "merchant", "stripe", "authorize", "authorize.net", "tranzila", "banquest"],
  },
  {
    id: "financial-software-integration",
    title: "Financial Software Integration",
    section: FINANCIAL,
    url: "/financial-setting/software-integration/view",
    keywords: ["quickbooks", "qb", "sage", "integration", "accounting software", "sync"],
  },
  {
    id: "accounts-receivable",
    title: "Accounts Receivable",
    section: FINANCIAL,
    url: "/financial-setting/accounts-receivable/view",
    keywords: ["account receivable", "receivable", "receivables", "ar"],
  },
  {
    id: "tax-id",
    title: "Tax ID",
    section: FINANCIAL,
    url: "/financial-setting/accounts-tax-id",
    keywords: ["tax", "ein", "tax number", "tax deductible", "501c3"],
  },

  // Members
  {
    id: "account-categories",
    title: "Account Categories",
    section: MEMBERS,
    url: "/financial-setting/members/account-types/view",
    keywords: ["account types", "member types", "membership types", "categories", "member category"],
  },
  {
    id: "dropdown-options",
    title: "Dropdown Options",
    section: MEMBERS,
    url: "/financial-setting/members/dropdown-options",
    keywords: ["dropdown", "options", "relationships", "custom fields", "lists"],
  },

  // Users & Permissions (only when the "user management" module is active)
  {
    id: "users",
    title: "Users & Permissions",
    section: "Users & Permissions",
    url: "/financial-setting?tab=users",
    keywords: ["users", "user", "permissions", "roles", "admins", "staff", "access", "invite user"],
    requiresModule: "user management",
  },

  // Communications
  {
    id: "communication-preferences",
    title: "Communication Preferences",
    section: "Communications",
    url: "/financial-setting?tab=communications&section=preferences",
    keywords: ["communications", "communication", "email settings", "reply to email", "sender", "preferences"],
  },
  {
    id: "twilio-setup",
    title: "Twilio Setup",
    section: "Communications",
    url: "/financial-setting?tab=communications&section=twilio",
    keywords: ["twilio", "sms", "text messages", "texting", "phone number"],
  },

  // Pastoral events (only when the "pastoral" module is active)
  {
    id: "pastoral-events-type",
    title: "Pastoral Events Type",
    section: "Pastoral Events Type",
    url: "/financial-setting?tab=pastoral",
    keywords: ["pastoral", "pastoral events", "event types", "easycare"],
    requiresModule: "pastoral",
  },
];

const readModules = (getter) => {
  try {
    return getter() ?? [];
  } catch (e) {
    return [];
  }
};

export const userModules = () =>
  readModules(() => JSON.parse(localStorage.getItem("authorizedModules") || "[]"));

export const orgModules = () =>
  readModules(() => JSON.parse(localStorage.getItem("organization") || "{}")?.authorizedModules);

export const isActive = (modules, name) => !!modules?.find?.((m) => m?.moduleName === name)?.active;

// Same rule as the /financial-setting route (src/Router/routes.js): the user's
// "settings" module and the organisation's "settings" module must both be active.
export const canSeeSettings = () => isActive(userModules(), "settings") && isActive(orgModules(), "settings");

// Same checks the Settings tabs use to show "Users & Permissions" / "Pastoral Events Type".
const entryAllowed = (entry) => !entry.requiresModule || isActive(userModules(), entry.requiresModule);

// Whether a saved Settings result (e.g. in recent searches) may still be shown to this user
export const isSettingsResultVisible = (item) =>
  canSeeSettings() && SETTINGS_SEARCH_ENTRIES.some((entry) => entry.url === item?.url && entry.title === item?.name && entryAllowed(entry));

const normalize = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// at most one typo (insert / delete / replace) for words of 5+ letters
const closeEnough = (a, b) => {
  if (a.length < 5 || Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (a.length < b.length) j++;
    else { i++; j++; }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

const MIN_QUERY_LENGTH = 2;

// Settings entries matching the typed text, best first. Empty when the user can't see Settings.
export const searchSettings = (query) => {
  if (!canSeeSettings()) return [];
  return rankEntries(SETTINGS_SEARCH_ENTRIES.filter(entryAllowed), query);
};

// Entries ({ title, section, keywords }) matching the typed text, best first.
// Shared by the Settings search and the pages search (pagesSearch.js).
export const rankEntries = (entries, query) => {
  const q = normalize(query);
  if (q.length < MIN_QUERY_LENGTH) return [];
  const queryWords = q.split(" ");

  return entries
    .map((entry) => {
      const title = normalize(entry.title);
      const titleWords = title.split(" ");
      const allWords = [...titleWords, ...normalize(entry.section).split(" "), ...entry.keywords.flatMap((k) => normalize(k).split(" "))];
      const phrases = [title, ...entry.keywords.map(normalize)];

      // every typed word must start (or almost match) one of the entry's words
      const everyWordMatches = queryWords.every((w) => allWords.some((word) => word.startsWith(w) || closeEnough(w, word)));
      if (!everyWordMatches) return null;

      let score = 1;
      if (phrases.some((p) => p === q)) score += 6; // exact title / keyword
      if (title.startsWith(q)) score += 4;
      if (queryWords.every((w) => titleWords.some((word) => word.startsWith(w)))) score += 3; // matches the title itself
      if (phrases.some((p) => p.startsWith(q))) score += 2;
      return { entry, score };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
    .map(({ entry }) => entry);
};
